import React, { Component } from 'react';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import i18n from 'i18n';
import { WithTranslation, withTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  changeInspectionViewActivePage,
  changeInspectionViewPageSize,
  setSearchKeyword,
} from 'store/analytics/ui/subViews/inspectionView/inspectionViewActions';
import { ArgumentPair, TxiViewRequest } from 'types/rest/analyticsTypes';
import {
  fetchInspectionViewRequestDetailThunk,
  fetchInspectionViewRequestsThunk,
  updateInspectionRequestThunk,
} from 'store/analytics/data/analyticsDataActions';
import { formatDateTimeFromMillis, formatDateTimeFromSeconds } from 'utils/dateUtil';
import MixpanelService from 'utils/mixpanelService';
import { REFRESH_INSPECTION_VIEW, SEARCH_FILTER_RESULTS } from 'utils/mixpanelEvents';
import { _findInArray, getTableOptions } from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewMar';
import { InspectionDataState } from 'store/analytics/data/analyticsDataTypes';
import TransactionInspectionDetails from 'views/Analytics/AnalyticsMockService/Inspection/RequestDetail/TransactionInspectionDetails';
import InspectionHeader from 'views/Analytics/AnalyticsMockService/Inspection/Header/InspectionHeader';
import styles from 'views/Analytics/AnalyticsMockService/Inspection/InspectionViewTransactional.module.css';
import clsx from 'clsx';
import SaveLiveRequestModal from 'views/Analytics/AnalyticsMockService/Inspection/SaveLiveRequestModal';
import { MockServiceAnalytics } from 'types/rest/mockServiceTypes';
import CopyToClipboardButton from 'components/buttons/CopyToClipboardButton';
import { getColoredHttpMethod, getPath } from 'utils/commonUtil';
import EditTransactionModal from '../../../../components/modal/EditTransactionModal';
import { Transaction } from '../../../../types/rest/assetCatalogTypes';
import { getTransaction } from '../../../../store/transactions/transactionsActions';
import { showToastNotification } from '../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../store/ui/toasts/toastsTypes';
import { getErrorMessage } from '../../../../utils/miscUtil';
import BzmIcon from '../../../../components/icons/BzmIcon';
import EditMessagingModal from '../../../../components/modal/EditMessagingModal';
import RunnersButtonGroup from '../../../../components/ui/ButtonGroups/RunnersButtonGroup';
import MessagingRunnerView from './MessagingRunnerView';
import { setActiveRunnerTab } from '../../../../store/analytics/ui/analyticsUiActions';
import { isFeatureEnabled } from '../../../../utils/bzmUserConfigUtil';

interface TxRequestListStateProps {
  isSaveTxnModalShown: boolean;
  /** Request to be saved in the modal */
  requestToBeSaved: TxiViewRequest | null;
  transaction: Transaction | null;
}

interface FilteredResults {
  requests: any[];
  tableOptions: BlazeTableOptions;
  sliceData: any;
}

interface TransactionInspectionViewProps {
  mockService: MockServiceAnalytics;
}

/**
 * Renders nested table of elements.
 * @param elements
 */
export const generateCondensedTableDom = (elements: string[]) => {
  return (
    <table className="table table-condensed table-striped mb-0">
      {elements.map((item, index) => (
        <tr key={item}>
          <td style={{ borderTop: index === 0 ? 'none' : undefined, paddingLeft: 0 }}>
            <span title={item} className="text-truncate">
              {item}
            </span>
          </td>
        </tr>
      ))}
    </table>
  );
};

/**
 * Renders cell with nested table.
 * @param txiViewArguments arguments wrapper from the backend. It is key value pair.
 * @param accessor key which will be used for generating list of values
 */
export const nestedTableCell = (txiViewArguments: ArgumentPair[] | null, accessor: string) => {
  if (_.isNil(txiViewArguments) || (txiViewArguments && txiViewArguments.length === 0)) {
    return null;
  }
  const listOfValues: string[] = txiViewArguments.map((item: any) => item[accessor]);
  if (listOfValues.length > 1) {
    return generateCondensedTableDom(listOfValues);
  } else {
    return listOfValues[0];
  }
};

/**
 * Request List.
 */
export class InspectionViewTransactional extends Component<AllProps, TxRequestListStateProps> {
  tableRef: any = React.createRef<BlazeTable>();

  state = {
    isSaveTxnModalShown: false,
    requestToBeSaved: null,
    /** transaction to be open in the modal */
    transaction: null,
  };

  componentDidMount(): void {
    this.getInspectionDataForFirstTime();
  }

  componentDidUpdate(prevProps: Readonly<AllProps>) {
    if (this.props.activeUiContext.activeRunnerTab !== prevProps.activeUiContext.activeRunnerTab) {
      this.callRefresh();
    } else {
      this.getInspectionDataForFirstTime();
    }
  }

  /**
   *  Reloads automatically requests for first time.
   */
  getInspectionDataForFirstTime = () => {
    if (
      this.props.activeDataContext &&
      this.props.activeUiContext &&
      this.props.activeUiContext.activeTab === 'inspection' &&
      this.props.activeDataContext.inspection.isLoadingRequests === false &&
      this.props.activeDataContext.inspection.requestsLoadedTs === null &&
      this.props.activeDataContext.inspection.isRequestSent === false
    ) {
      this.props.refreshRequests(this.props.activeTab);
    }
  };

  callRefresh = () => {
    MixpanelService.track(REFRESH_INSPECTION_VIEW(this.props.activeTab));
    this.props.refreshRequests(this.props.activeTab);
  };

  createTransactionFromRealTrafficHandler = (request: any) => {
    this.setState({
      isSaveTxnModalShown: true,
      requestToBeSaved: request,
    });
  };

  openTxnDetailHandler = async (txnId: string, e: any) => {
    //Do not load analytics detail
    e.stopPropagation();

    //Get txn
    try {
      const txn = (await getTransaction(txnId)).data.result;
      this.setState({ transaction: txn });
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      this.props.showToastNotification(ToastType.Danger, 'Cannot retrieve transaction', 'Reason: ' + errorMsg);
    }
  };

  // Definition of table columns
  colsDefinition: BlazeTableColDef[] = [
    {
      header: i18n.t('analytics:txInspection.table.method'),
      width: 80,
      accessor: 'operation',
      cellComponent: (request: TxiViewRequest) => {
        return getColoredHttpMethod(request.operation as any);
      },
    },
    {
      header: i18n.t('analytics:txInspection.table.requestPath'),
      cellComponent: (request: TxiViewRequest) => {
        return (
          <div id={`requestPathValue` + request.id} title={request.url}>
            {!_.isNil(request.url) ? getPath(request.url) : null}
            <div className="flex-table-actions ml-2">
              <CopyToClipboardButton
                textToCopy={request.url}
                title={i18n.t('analytics:txInspection.table.copyRequestPath')}
                id={`requestPathCopyButton` + request.id}
              >
                <span
                  className="icon-bzm-clipboard btn-link-dark icon-xs text-blue"
                  aria-hidden="true"
                  style={{ color: 'inherit' }}
                />
              </CopyToClipboardButton>
            </div>
          </div>
        );
      },
      accessor: 'url',
    },
    {
      header: i18n.t('analytics:txInspection.table.txnId'),
      cellComponent: (request: TxiViewRequest) => {
        if (!_.isNil(request.matched) && request.matched === true) {
          return (
            <button
              onClick={e => this.openTxnDetailHandler(request.transactionId, e)}
              className={'btn btn-link no-hover'}
              style={{ padding: 0 }}
            >
              {request.transactionId ? request.transactionId.toString() : null}
            </button>
          );
        } else {
          return 'no match';
        }
      },
      width: 120,
      accessor: 'transactionId',
    },
    {
      header: i18n.t('analytics:txInspection.table.timestamp'),
      accessor: 'timestamp',
      width: 180,
      cellComponent: (rowData: TxiViewRequest) => {
        if (_.isNil(rowData.timestamp)) {
          return 'N/A';
        }
        const dateTimeWithMillis = formatDateTimeFromMillis(parseInt(rowData.timestamp));
        return (
          <span title={dateTimeWithMillis}>{formatDateTimeFromSeconds(parseInt(rowData.timestamp) / 1000, true)}</span>
        );
      },
    },
    {
      header: i18n.t('analytics:txInspection.table.txnPriority'),
      width: 100,
      accessor: 'txnPriority',
    },
    {
      header: i18n.t('analytics:txInspection.table.queryParameter'),
      cellComponent: (request: TxiViewRequest) => {
        return nestedTableCell(request.arguments, 'key');
      },
      accessor: 'queryParameter',
    },
    {
      header: i18n.t('analytics:txInspection.table.queryParameterValue'),
      cellComponent: (request: TxiViewRequest) => {
        return nestedTableCell(request.arguments, 'value');
      },
      accessor: 'queryParameterValue',
    },
    {
      header: i18n.t('analytics:txInspection.table.source'),
      width: 70,
      accessor: 'source',
      cellComponent: (request: TxiViewRequest) => {
        switch (request.source) {
          case 'MOCK':
            return <span className={clsx('badge badge-pill badge-primary', styles.badge)}>Mock</span>;
          case 'LIVE':
            return <span className={clsx('badge badge-pill badge-success', styles.badge)}>Live</span>;
        }
      },
    },
    {
      header: '',
      width: 50,
      cellComponent: (request: TxiViewRequest) => {
        if (request.source === 'LIVE')
          return (
            <div className={'flex-table-actions'}>
              <span
                title={
                  request.liveTransactionCreated !== true
                    ? 'Create new transactions based on the real traffic'
                    : 'Already saved'
                }
              >
                <button
                  disabled={request.liveTransactionCreated !== false}
                  className={'btn btn-link btn-no-hover icon-xs'}
                  onClick={() => this.createTransactionFromRealTrafficHandler(request)}
                >
                  <BzmIcon variant={'icon-bzm-save'} />
                </button>
              </span>
            </div>
          );
      },
      accessor: 'queryParameterValue',
    },
  ];

  filterRequestList = (searchKeyword: string) => {
    const requests = this.getRequestsFromDataContext();

    if (!_.isEmpty(searchKeyword)) {
      return requests.filter((item: TxiViewRequest) => {
        return (
          (item.operation ? item.operation.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.url ? item.url.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.id
            ? item.id
                .toString()
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.transactionId
            ? item.transactionId
                .toString()
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.timestamp
            ? formatDateTimeFromSeconds(parseInt(item.timestamp) / 1000)
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.source ? item.source.toLowerCase().includes(searchKeyword.toLowerCase()) : false) ||
          (item.serviceMockId
            ? item.serviceMockId
                .toString()
                .toLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          (item.txnPriority
            ? item.txnPriority
                .toString()
                .toLocaleLowerCase()
                .includes(searchKeyword.toLowerCase())
            : false) ||
          _findInArray(item.arguments, searchKeyword)
        );
      });
    } else {
      return requests;
    }
  };

  handleFilterChange = async (value: string) => {
    this.props.setSearchKeyword(value);
    this.sendMixPanelEvent();
  };

  sendMixPanelEvent = _.debounce(() => {
    MixpanelService.track(SEARCH_FILTER_RESULTS());
  }, 600);

  /**
   * Gets list of request from active data context in redux.
   */
  getRequestsFromDataContext = () => {
    if (_.isNil(this.props.activeDataContext)) {
      return [];
    }
    const txiDataState: InspectionDataState = this.props.activeDataContext.inspection;
    return (txiDataState.requests as TxiViewRequest[]) || [];
  };

  hideModalHandler = () => {
    this.setState({
      isSaveTxnModalShown: false,
    });
  };

  onTransactionCreatedHandler = async (request: TxiViewRequest) => {
    //Update this txn in redux store
    await this.props.updateInspectionRequestThunk(request);
  };

  render() {
    // UI state
    const activeUiContext = this.props.activeUiContext;
    const activePage = activeUiContext ? activeUiContext.inspectionView.activePage : 1;
    const pageSize = activeUiContext ? activeUiContext.inspectionView.pageSize : 5;
    const selectedRequest = activeUiContext ? activeUiContext.inspectionView.selectedRequest : null;
    const searchKeyword = activeUiContext ? activeUiContext.inspectionView.searchKeyword : '';

    // Data state
    const activeDataContext = this.props.activeDataContext;
    const isRequestListLoading = activeDataContext ? activeDataContext.inspection.isLoadingRequests : false;
    const lastRefresh =
      activeDataContext !== null &&
      activeDataContext.inspection !== null &&
      activeDataContext.inspection.requestsLoadedTs !== null
        ? new Date(activeDataContext.inspection.requestsLoadedTs)
        : null;
    const tabId = activeDataContext ? activeDataContext.tabId : null;

    const requests = this.getRequestsFromDataContext();

    const tableOptions: BlazeTableOptions = {
      showRowExpander: false,
      columnDef: this.colsDefinition,
      pageable: true,
      sortable: true,
      selectable: false,
      pageSize: 5,
      minWidth: 950,
      minHeight: 210,
      tableName: 'requestList',
      noResultsText: i18n.t('analytics:inspection.noResultsText'),
      customHeaderClassName: 'flex-table-item-top',
      customTableClassName: 'flex-table-simple flex-table-condensed',
      onRowClick: (requestId: number) => this.props.fetchInspectionViewRequestDetailThunk(requestId, tabId),
      customRowClassName: (request: any) => {
        let rowClass = 'hand-cursor ';
        if (selectedRequest && request.id.toString() === selectedRequest.id.toString()) {
          rowClass += styles.customEventClass;
        }
        return rowClass;
      },
      maxContentHeight: '186px',
      paginationOverride: {
        pageSize: pageSize,
        total: requests.length,
        activePage: activePage,
      },
      hidePager: requests.length === 0,
    };

    const getInspectionHeader = (title: string) => (
      <InspectionHeader
        refreshHandler={this.callRefresh}
        requests={requests}
        onSearchKeywordChanged={this.handleFilterChange}
        lastRefresh={lastRefresh}
        searchKeyword={searchKeyword}
        title={title}
        msType={this.props.mockService.type}
      ></InspectionHeader>
    );

    const getHttpRunnerView = () => {
      const initResult = this.filterRequestList(searchKeyword);
      const filterResult: FilteredResults = getTableOptions(initResult, searchKeyword, tableOptions, pageSize);
      return (
        <>
          {getInspectionHeader(i18n.t('analytics:inspection.requests'))}
          <BlazeTable
            isLoading={isRequestListLoading}
            options={filterResult.tableOptions}
            onPageSizeChange={this.props.changeInspectionViewPageSize}
            onPageChange={this.props.changeInspectionViewActivePage}
            data={filterResult.sliceData}
            onRef={(ref: any) => (this.tableRef = ref)}
            showPageSizeDropdown={true}
          />
          {/* Detail for selected request, show it only if there is detail selected */}
          {!_.isNil(selectedRequest) && !_.isNil(lastRefresh) && <TransactionInspectionDetails />}
        </>
      );
    };

    const getMessagingRunnerView = () => (
      <>
        {getInspectionHeader(i18n.t('analytics:inspection.inboundMessages'))}
        <MessagingRunnerView setTransaction={txn => this.setState({ transaction: txn })} />
      </>
    );

    return (
      <>
        {/*TODO - remove duplicates*/}
        {!_.isNil(this.state.transaction) &&
          (this.state.transaction.type === 'HTTP' ? (
            <EditTransactionModal
              isReadOnly={false}
              isNew={false}
              transaction={this.state.transaction as any}
              onClose={() => {
                this.setState({ transaction: null });
              }}
            ></EditTransactionModal>
          ) : (
            <EditMessagingModal
              isReadOnly={false}
              isNew={false}
              transaction={this.state.transaction as any}
              onClose={() => {
                this.setState({ transaction: null });
              }}
            ></EditMessagingModal>
          ))}

        <SaveLiveRequestModal
          onHide={this.hideModalHandler}
          isModalShown={this.state.isSaveTxnModalShown}
          request={this.state.requestToBeSaved}
          mockServiceAnalytics={this.props.mockService}
          onTransactionCreated={this.onTransactionCreatedHandler}
        />

        <>
          <div className={'mb-4'}>
            <RunnersButtonGroup
              activeRunner={this.props.activeUiContext.activeRunnerTab}
              onRunnerChanged={activeRunner => {
                this.props.setActiveRunnerTab(activeRunner);
              }}
            />
          </div>

          {this.props.activeUiContext.activeRunnerTab === 'httpRunner' ? getHttpRunnerView() : getMessagingRunnerView()}
        </>
      </>
    );
  }
}

const mapStateToProps = ({ analytics }: ApplicationState) => {
  return {
    activeTab: analytics.ui.activeTab,
    activeUiContext: analytics.ui.activeUiContext,
    activeDataContext: analytics.data.activeDataContext,
  };
};

const mapDispatchToProps = {
  changeInspectionViewActivePage,
  changeInspectionViewPageSize,
  fetchInspectionViewRequestDetailThunk,
  setSearchKeyword: setSearchKeyword,
  updateInspectionRequestThunk,
  refreshRequests: fetchInspectionViewRequestsThunk,
  showToastNotification,
  setActiveRunnerTab,
};

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type AllProps = MapStateToProps & WithTranslation & DispatchProps & TransactionInspectionViewProps;

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(InspectionViewTransactional));
